import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Building2,
  Store,
  Home,
  Users,
  ScrollText,
  FileText,
  Flag,
  Package,
  Activity,
  CreditCard,
  Layers,
  MessageSquare,
  Wallet,
  ReceiptText,
  BookText,
  BarChart3,
  Coins,
  Award,
  MailPlus,
  Network,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Logo } from "@/shared/components/brand/Logo";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Permission, hasPermission } from "@/shared/auth/permissions";
import { Section, canAccessSection } from "@/app/section-access";

interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: string;
  disabled?: boolean;
  /** Shown only to the roles `SECTION_ROLES` lists for this section (mirrors the API gates). */
  section?: Section;
  /** Shown only when the session holds this permission (from `/auth/me`). */
  permission?: Permission;
}

const navItems: NavItem[] = [
  {
    to: "/dashboard",
    icon: LayoutDashboard,
    labelKey: "nav.dashboard",
    section: Section.DASHBOARD,
  },
  { to: "/venues", icon: Building2, labelKey: "nav.venues", section: Section.VENUE_APPROVAL },
  {
    to: "/venues-directory",
    icon: Store,
    labelKey: "nav.venuesDirectory",
    section: Section.VENUE_DIRECTORY,
  },
  { to: "/properties", icon: Home, labelKey: "nav.properties", section: Section.PROPERTIES },
  { to: "/users", icon: Users, labelKey: "nav.users", section: Section.USERS },
  {
    to: "/invitations",
    icon: MailPlus,
    labelKey: "nav.invitations",
    permission: Permission.ORG_MEMBERS_READ,
  },
  {
    to: "/organizations",
    icon: Network,
    labelKey: "nav.organizations",
    permission: Permission.ORG_MEMBERS_READ,
  },
  { to: "/audit-log", icon: ScrollText, labelKey: "nav.auditLog", disabled: true },
  {
    to: "/compliance/documents",
    icon: FileText,
    labelKey: "nav.compliance",
    section: Section.COMPLIANCE,
  },
  {
    to: "/communications",
    icon: MessageSquare,
    labelKey: "nav.communications",
    section: Section.COMMUNICATIONS,
  },
  {
    to: "/subscriptions",
    icon: CreditCard,
    labelKey: "nav.subscriptions",
    section: Section.SUBSCRIPTIONS,
  },
  {
    to: "/founding",
    icon: Award,
    labelKey: "nav.founding",
    section: Section.FOUNDING,
  },
  {
    to: "/subscription-tiers",
    icon: Layers,
    labelKey: "nav.subscriptionTiers",
    section: Section.SUBSCRIPTION_TIERS,
  },
  {
    to: "/feature-flags",
    icon: Flag,
    labelKey: "nav.featureFlags",
    section: Section.FEATURE_FLAGS,
  },
  {
    to: "/catalog",
    icon: Package,
    labelKey: "nav.catalog",
    section: Section.CATALOG,
  },
  {
    to: "/drift-alerts",
    icon: Activity,
    labelKey: "nav.driftAlerts",
    section: Section.DRIFT_ALERTS,
  },
  {
    to: "/finance/payouts",
    icon: Wallet,
    labelKey: "nav.finance",
    section: Section.FINANCE,
  },
  {
    to: "/finance/reports",
    icon: BarChart3,
    labelKey: "nav.financialReports",
    section: Section.FINANCE,
  },
  {
    to: "/finance/accruals",
    icon: Coins,
    labelKey: "nav.accruals",
    section: Section.FINANCE,
  },
  {
    to: "/finance/ledger",
    icon: BookText,
    labelKey: "nav.ledger",
    section: Section.FINANCE,
  },
  {
    to: "/finance/ledger-corrections",
    icon: ReceiptText,
    labelKey: "nav.ledgerCorrections",
    section: Section.FINANCE,
  },
];

export function Sidebar() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const visibleNavItems = navItems.filter(
    (item) =>
      (!item.section || canAccessSection(currentUser?.role, item.section)) &&
      (!item.permission || hasPermission(currentUser, item.permission)),
  );
  return (
    <aside
      data-testid="sidebar"
      className="hidden w-64 flex-shrink-0 flex-col border-r border-white/5 bg-wander-black text-white md:flex"
    >
      <div className="flex h-20 items-center border-b border-white/10 px-6">
        <Logo variant="dark" size="sm" />
      </div>
      <div className="px-6 pt-6">
        <p className="brand-eyebrow">{t("nav.sectionLabel")}</p>
      </div>
      <nav className="flex-1 space-y-1 p-3" aria-label={t("nav.primary")}>
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const navId = item.to.replace(/^\//, "");
          if (item.disabled) {
            return (
              <div
                key={item.to}
                data-testid={`sidebar-${navId}-disabled`}
                className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-white/30"
                aria-disabled
              >
                <Icon className="h-4 w-4" />
                <span className="font-sans font-normal">{t(item.labelKey)}</span>
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`navigate-to-${navId}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-wander-orange font-bold text-white shadow-[0_6px_20px_-8px_rgba(249,115,22,0.6)]"
                    : "font-normal text-white/70 hover:bg-white/5 hover:text-white",
                )
              }
            >
              <Icon className="h-4 w-4" />
              <span className="font-sans">{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-white/10 px-6 py-4">
        <p className="font-sans text-xs text-white/40">{t("app.tagline")}</p>
      </div>
    </aside>
  );
}
